import { ToasterService } from '#services';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

interface ToastrMessage {
  detail: string;
  summary: string;
}

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private _toastrService = inject(ToasterService);
  private _translateService = inject(TranslateService);
  private _router = inject(Router);
  private readonly _chunkReloadStorageKey = 'centaur.chunkReloadAttempted';

  handleError(err: unknown): void {
    console.error(err);

    if (this._isChunkLoadError(err)) {
      this._reloadAfterChunkLoadError();
      return;
    }

    // status 0 = network-level failure (ERR_CONNECTION_REFUSED, server down, etc.)
    if (err instanceof HttpErrorResponse && err.status === 0) {
      this._router.navigate(['/500']);
      return;
    }

    const { detail, summary = '' } = this._handle(err);

    this._toastrService.showError(detail, summary);
  }

  private _handle(err: unknown): ToastrMessage {
    if (err instanceof HttpErrorResponse && err.status === 403) {
      return {
        detail: this._translateService.instant(
          'common.forbidActionErrorMessage',
        ),
        summary: '',
      };
    }

    if (err instanceof HttpErrorResponse && err.error) {
      const { ExceptionMessage = '', Title = '' } = err.error;

      if (Title !== '' && ExceptionMessage !== '') {
        return {
          detail: ExceptionMessage,
          summary: Title,
        };
      }
    }

    return {
      detail: this._translateService.instant('common.genericErrorMessage'),
      summary: '',
    };
  }

  private _isChunkLoadError(err: unknown): boolean {
    if (!err) return false;

    const error = err as { message?: unknown; name?: unknown; rejection?: unknown };
    const message = typeof error.message === 'string' ? error.message : '';
    const name = typeof error.name === 'string' ? error.name : '';

    if (
      name === 'ChunkLoadError' ||
      message.includes('ChunkLoadError') ||
      message.includes('Loading chunk') ||
      message.includes('Failed to fetch dynamically imported module')
    ) {
      return true;
    }

    return this._isChunkLoadError(error.rejection);
  }

  private _reloadAfterChunkLoadError(): void {
    const currentUrl = globalThis.location?.href ?? '';
    const lastReloadUrl = globalThis.sessionStorage?.getItem(this._chunkReloadStorageKey);

    if (lastReloadUrl === currentUrl) {
      globalThis.sessionStorage?.removeItem(this._chunkReloadStorageKey);
      this._toastrService.showError(
        this._translateService.instant('common.genericErrorMessage'),
      );
      return;
    }

    globalThis.sessionStorage?.setItem(this._chunkReloadStorageKey, currentUrl);
    globalThis.location.reload();
  }
}
